/**
 * Dexie/IndexedDB schema for the local event cache and outbound sync queue
 * (ADR-0004 client queue, ADR-0001 `src/data` ownership).
 *
 * Two object stores:
 * - `events`: the full local log, keyed by `eventId` (dedupe is a Dexie
 *   `put` with the primary key, matching the server's `INSERT OR IGNORE`
 *   idempotency, ADR-0005).
 * - `outbox`: FIFO outbound queue of `eventId`s not yet confirmed pushed.
 *   Kept as a separate store (rather than a boolean flag on `events`) so
 *   `peekQueue`/`markPushed` are cheap, ordered reads/deletes independent of
 *   the full log's size.
 * - `meta`: single-row key/value store for the sync cursor and quarantine
 *   list.
 */

import Dexie, { type Table } from 'dexie';
import type { CompassEvent, EventId } from '../../contracts';

/** One row in the `events` store. `eventId` is the primary key. */
export interface StoredEventRow {
  readonly eventId: EventId;
  readonly event: CompassEvent;
}

/** One row in the `outbox` store, in FIFO enqueue order via `seq`. */
export interface OutboxRow {
  readonly seq?: number;
  readonly eventId: EventId;
}

export interface MetaRow {
  readonly key: string;
  readonly value: unknown;
}

export const META_KEY_CURSOR = 'syncCursor';
export const META_KEY_QUARANTINE = 'quarantinedEventIds';

export class CompassDb extends Dexie {
  events!: Table<StoredEventRow, EventId>;
  outbox!: Table<OutboxRow, number>;
  meta!: Table<MetaRow, string>;

  constructor(name = 'compass') {
    super(name);
    this.version(1).stores({
      events: 'eventId',
      outbox: '++seq, eventId',
      meta: 'key',
    });
  }
}
